/**
 * One vertical card per service group on the public status page.
 *
 * Inside the card we list services in their registry order, optionally
 * partitioned into sub-groups. Each row shows the name + description +
 * current status badge + 90-day uptime bar.
 *
 * "External" services are rendered with a distinct, lighter treatment
 * (gray dot + "External" label, no probe latency, no uptime bar) so the
 * page is honest about what's actually being monitored.
 */

import type {
  ServiceCard,
  ServiceStatus,
  UptimeServiceSummary,
} from "../api/status";
import { UptimeBar } from "./UptimeBar";

interface Props {
  group: string;
  services: ServiceCard[];
  uptimeByKey: Map<string, UptimeServiceSummary>;
  spanDays: number;
}

const STATUS_DOT: Record<ServiceStatus, string> = {
  operational: "bg-status-ok",
  degraded: "bg-status-degraded",
  partial_outage: "bg-status-partial",
  major_outage: "bg-status-major",
  unknown: "bg-status-unknown",
};

const STATUS_LABEL: Record<ServiceStatus, string> = {
  operational: "Operational",
  degraded: "Degraded",
  partial_outage: "Partial outage",
  major_outage: "Major outage",
  unknown: "Unknown",
};

export function ServiceGroupCard({
  group,
  services,
  uptimeByKey,
  spanDays,
}: Props): JSX.Element {
  // Partition into subgroups while preserving registry order.
  const subgroups = new Map<string, ServiceCard[]>();
  for (const s of services) {
    const key = s.subgroup ?? "";
    const arr = subgroups.get(key) ?? [];
    arr.push(s);
    subgroups.set(key, arr);
  }

  // Roll up group-level summary: count of operational vs total
  // monitored services in this group.
  const monitored = services.filter((s) => !s.external);
  const okCount = monitored.filter((s) => s.status === "operational").length;
  const allOk = monitored.length > 0 && okCount === monitored.length;
  const anyDown = monitored.some(
    (s) => s.status === "major_outage" || s.status === "partial_outage",
  );
  const summary =
    monitored.length === 0
      ? `${services.length} external`
      : allOk
        ? `${okCount}/${monitored.length} operational`
        : anyDown
          ? `${monitored.length - okCount} ${
              monitored.length - okCount === 1 ? "issue" : "issues"
            }`
          : `${okCount}/${monitored.length} operational`;

  return (
    <article className="rounded-2xl border border-white/[0.07] bg-dr-surface/60 p-6 shadow-sm backdrop-blur-sm">
      <header className="mb-5 flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-dr-heading">{group}</h2>
        <span className="text-xs text-text-muted">{summary}</span>
      </header>

      <div className="space-y-6">
        {[...subgroups.entries()].map(([sub, rows]) => (
          <div key={sub || "__default__"}>
            {sub && (
              <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-text-muted">
                {sub}
              </p>
            )}
            <ul className="space-y-4">
              {rows.map((s) => (
                <ServiceRow
                  key={s.key}
                  service={s}
                  uptime={uptimeByKey.get(s.key)}
                  spanDays={spanDays}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </article>
  );
}

function ServiceRow({
  service,
  uptime,
  spanDays,
}: {
  service: ServiceCard;
  uptime: UptimeServiceSummary | undefined;
  spanDays: number;
}): JSX.Element {
  if (service.external) {
    return (
      <li>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-text-secondary">
              {service.displayName}
            </p>
            {service.description && (
              <p className="text-xs text-text-muted">{service.description}</p>
            )}
          </div>
          <span className="pill bg-white/[0.04] text-text-muted">
            <span
              className="h-2 w-2 rounded-full bg-text-muted/60"
              aria-hidden
            />
            External
          </span>
        </div>
      </li>
    );
  }

  return (
    <li>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-dr-heading">
            {service.displayName}
          </p>
          {service.description && (
            <p className="text-xs text-text-muted">{service.description}</p>
          )}
        </div>
        <span className="pill bg-bg-elevated text-text-secondary">
          <span
            className={`h-2 w-2 rounded-full ${STATUS_DOT[service.status]}`}
            aria-hidden
          />
          {STATUS_LABEL[service.status]}
          {service.lastLatencyMs !== null && (
            <span className="ml-1 text-text-muted">
              · {service.lastLatencyMs}ms
            </span>
          )}
        </span>
      </div>
      <UptimeBar days={uptime?.days ?? []} spanDays={spanDays} />
      <div className="mt-1 flex items-center justify-between text-[10px] text-text-muted">
        <span>{spanDays}d ago</span>
        <span className="font-mono text-text-secondary">
          {(uptime?.uptimePct ?? 100).toFixed(2)}%
        </span>
        <span>Today</span>
      </div>
    </li>
  );
}
